import { createSignal, onMount, onCleanup, For, Show, createEffect } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import Text from '../components/Text';
import AddonState from '../components/AddonState';
import StateDisplay from '../components/StateDisplay';
import { useUserContext } from '../components/UserContextProvider';
import { useRoleParser } from '../hooks/permissions';
import { useSFetch } from '../hooks/sfetch';
import styles from './Dashboard.module.css';

interface BotInfo {
    id: string;
    username: string;
}

export default function Dashboard() {
    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const sfetch = useSFetch();

    const [grantedBots, setGrantedBots] = createStore<BotInfo[]>([]);
    const [allActiveBots, setAllActiveBots] = createStore<BotInfo[]>([]);
    const [loadingBots, setLoadingBots] = createSignal(true);

    const isAdmin = () => {
        const userRoles = parseRoles(user()?.permissions || 0, roles);
        return userRoles[3] && user().showAdminUiInMemberApp; // Admin is at index 3
    };

    const loadBots = async () => {
        try {
            const grantedResponse = await sfetch('/api/GrantedUsers/GetGrantedBots');
            if (grantedResponse.ok) {
                if (grantedResponse.value) {
                    setGrantedBots(reconcile(grantedResponse.value, { key: 'id' }));
                }
            }

            const userIsAdmin = isAdmin();

            if (userIsAdmin) {
                const allBotsResponse = await sfetch('/api/GrantedUsers/GetAllActiveBots');
                if (allBotsResponse.ok) {
                    if (allBotsResponse.value) {
                        setAllActiveBots(reconcile(allBotsResponse.value, { key: 'id' }));
                    }
                }
            }
        } catch (_error) {
        } finally {
            setLoadingBots(false);
        }
    };

    createEffect(() => {
        if (user()?.loggedin) {
            loadBots();
        }
    });

    onMount(() => {
        const interval = setInterval(loadBots, 10000);
        onCleanup(() => clearInterval(interval));
    });

    return (
        <div class={styles.dashboardContainer}>
            <h1><Text val="DASHBOARD"></Text></h1>

            <div class={styles.section}>
                <h2><Text val="MYBOTS"></Text></h2>
                <Show when={loadingBots()}>
                    <StateDisplay loading message="DASHBOARD_LOADING" />
                </Show>
                <Show when={!loadingBots() && grantedBots.length === 0}>
                    <StateDisplay empty icon="fa-solid fa-robot" message="NOBOTS" helpText="DASHBOARD_NO_BOTS_HELP" />
                </Show>
                <Show when={!loadingBots() && grantedBots.length > 0}>
                    <div class={styles.botGrid}>
                        <For each={grantedBots}>
                            {(bot) => (
                                <div class={styles.botCardWrapper}>
                                    <h3 class={styles.botName}>{bot.username}</h3>
                                    <AddonState id={bot.id} />
                                </div>
                            )}
                        </For>
                    </div>
                </Show>
            </div>

            <Show when={isAdmin()} fallback={<></>}>
                <div class={styles.section}>
                    <h2><Text val="ALLACTIVEBOTS"></Text></h2>
                    <Show
                        when={allActiveBots.length > 0}
                        fallback={<p class={styles.noBots}><Text val="NOBOTS"></Text> (Admin can see all bots here)</p>}
                    >
                        <div class={styles.botGrid}>
                            <For each={allActiveBots}>
                                {(bot) => (
                                    <div class={styles.botCardWrapper}>
                                        <h3 class={styles.botName}>{bot.username}</h3>
                                        <AddonState id={bot.id} />
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </div>
            </Show>
        </div>
    );
}